import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import apiService from '../../../services/apiService';
import { Button } from '../../../components/ui';
import { FaSave, FaTimes } from 'react-icons/fa';
import { useToast } from '../../../context/ToastContext';

const breadcrumb = (
  <span>
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Home
    </span> &nbsp;/&nbsp; 
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Course Programs
    </span> &nbsp;/&nbsp; 
    <span className="text-gray-700">Add New</span>
  </span>
);

const AddCourseProgram = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue, watch } = useForm({
    defaultValues: {
      name: '',
      desci: '',
      icon: '',
      thumb: '',
      startDate: '',
      endDate: '',
      link: '',
      uri: '',
      eduStandard: '',
      course: '',
      status: true,
      viewFile: '',
      order: 0,
      cost: '',
      MRP: '',
      metaTags: {
        keywords: '',
        ogDescription: '',
        ogImage: '',
        ogTitle: ''
      }
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [eduStandards, setEduStandards] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await apiService.request('/i/ims/course?limit=all&status=true&select=name,eduStandard,eduStandards&populate=eduStandard&select_eduStandard=name');
        if (Array.isArray(data)) {
          setCourses(data);
        } else {
          toast.showWarning('Received unexpected course data format');
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
        toast.showError('Failed to load courses');
      }
    };

    fetchCourses();
  }, []);

  // Fetch education standards
  useEffect(() => {
    const fetchEduStandards = async () => {
      try {
        const data = await apiService.request('/i/ims/edustandard?limit=all&status=true&select=name');
        if (Array.isArray(data)) {
          setEduStandards(data);
        } else {
          toast.showWarning('Received unexpected education standard data format');
        }
      } catch (error) {
        console.error('Error fetching education standards:', error);
        toast.showError('Failed to load education standards');
      }
    };

    fetchEduStandards();
  }, []);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      // Format dates if needed
      if (data.startDate) {
        const [day, month, year] = data.startDate.split('/');
        data.startDate = `${day}/${month}/${year}`;
      }
      
      if (data.endDate) {
        const [day, month, year] = data.endDate.split('/');
        data.endDate = `${day}/${month}/${year}`;
      }
      
      // Convert status to boolean if it's a string
      if (typeof data.status === 'string') {
        data.status = data.status === 'true';
      }
      
      // Convert order to number
      data.order = Number(data.order) || 0;
      
      console.log('Submitting data:', data);
      await apiService.request('/i/ims/courseprogram', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      toast.showSuccess('Course program created successfully');
      navigate('/course-programs');
    } catch (error) {
      console.error('Error creating course program:', error);
      toast.showError('Failed to create course program. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="border-b border-gray-200">
        <div className="p-6">
          <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
            {breadcrumb}
          </nav>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Add Course Program</h1>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('basic')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'basic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Basic Details
              </button>
              <button
                onClick={() => setActiveTab('meta')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'meta'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Meta Tags
              </button>
            </nav>
          </div>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            {activeTab === 'basic' && (
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('name', { required: 'Name is required' })}
                    placeholder="Enter program name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="uri">
                    URI <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="uri"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('uri', { required: 'URI is required' })}
                    placeholder="e.g., class-10th"
                  />
                  {errors.uri && (
                    <p className="text-red-500 text-sm mt-1">{errors.uri.message}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="course">
                    Course
                  </label>
                  <select
                    id="course"
                    className="w-full border rounded px-3 py-2"
                    {...register('course')}
                    disabled={courses.length === 0}
                  >
                    <option value="">{courses.length === 0 ? 'Loading courses...' : 'Select Course'}</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="eduStandard">
                    Education Standard
                  </label>
                  <select
                    id="eduStandard"
                    className="w-full border rounded px-3 py-2"
                    {...register('eduStandard')}
                    disabled={eduStandards.length === 0}
                  >
                    <option value="">{eduStandards.length === 0 ? 'Loading standards...' : 'Select Education Standard'}</option>
                    {eduStandards.map((std) => (
                      <option key={std.id} value={std.id}>
                        {std.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="desci">
                    Description
                  </label>
                  <textarea
                    id="desci"
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                    {...register('desci')}
                    placeholder="Enter program description"
                  ></textarea>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="thumb">
                      Thumbnail
                    </label>
                    <input
                      id="thumb"
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Image URL"
                      {...register('thumb')}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="icon">
                      Icon
                    </label>
                    <input
                      id="icon"
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Icon URL"
                      {...register('icon')}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="startDate">
                      Start Date
                    </label>
                    <input
                      id="startDate"
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      placeholder="DD/MM/YYYY"
                      {...register('startDate')}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="endDate">
                      End Date
                    </label>
                    <input
                      id="endDate"
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      placeholder="DD/MM/YYYY"
                      {...register('endDate')}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="link">
                    Link
                  </label>
                  <input
                    id="link"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    placeholder="External link"
                    {...register('link')}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="cost">
                      Cost
                    </label>
                    <input
                      id="cost"
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Enter cost"
                      {...register('cost')}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="MRP">
                      MRP
                    </label>
                    <input
                      id="MRP"
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Enter MRP"
                      {...register('MRP')}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="order">
                    Order
                  </label>
                  <input
                    id="order"
                    type="number"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Display order"
                    {...register('order')}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="status">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center">
                    <div className="flex items-center mr-4">
                      <input
                        id="statusActive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={watch('status') === true || watch('status') === 'true'}
                        onChange={() => {
                          setValue('status', true);
                          console.log('Set status to true');
                        }}
                      />
                      <label htmlFor="statusActive" className="ml-1">Active</label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="statusInactive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={watch('status') === false || watch('status') === 'false'}
                        onChange={() => {
                          setValue('status', false);
                          console.log('Set status to false');
                        }}
                      />
                      <label htmlFor="statusInactive" className="ml-1">Inactive</label>
                    </div>
                  </div>
                  {errors.status && (
                    <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'meta' && (
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogTitle">
                    OG Title
                  </label>
                  <input
                    id="metaTags.ogTitle"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('metaTags.ogTitle')}
                    placeholder="e.g., My Awesome Course Program"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.keywords">
                    Keywords
                  </label>
                  <input
                    id="metaTags.keywords"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('metaTags.keywords')}
                    placeholder="e.g., education, course, program"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogDescription">
                    OG Description
                  </label>
                  <textarea
                    id="metaTags.ogDescription"
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                    {...register('metaTags.ogDescription')}
                    placeholder="Enter meta description"
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogImage">
                    OG Image
                  </label>
                  <input
                    id="metaTags.ogImage"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Image URL"
                    {...register('metaTags.ogImage')}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <Button
                type="button"
                onClick={() => navigate('/course-programs')}
                variant="outline"
                size="md"
                className="border-gray-300 text-gray-700"
                icon={FaTimes}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={isSubmitting || loading}
                icon={FaSave}
                className={`${(isSubmitting || loading) ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting || loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCourseProgram;
